<div class="multiple-price-changes-container no-select">
  <div class="widget-div">
    @if (canRulesBeViewed) {
      <app-shared-widget-container-component>
        <div title class="widget-div-heading">
          <app-display-icon
            [tooltip]="canRulesBeEdited ? tooltips.editAccessGranted : tooltips.editAccessDenied"
            [icon]="canRulesBeEdited ? icons.lockOpenOutline : icons.lockClosedOutline">
          </app-display-icon>
          <div class="heading-copy">
            <div>Multiple Price Change Rules</div>
            <p class="heading-subtitle">
              Retail price 1 is always the master price. These rules decide how prices 2-10 follow it when a price or GP is edited.
            </p>
          </div>
        </div>

        <div block class="multiple-price-block">
          @if (isSaving()) {
            <span class="source-note">Saving multiple price change rules…</span>
          } @else if (isLoading()) {
            <span class="source-note">Loading multiple price change rules…</span>
          }

          <section class="enable-card">
            <div class="enable-row">
              <div class="enable-copy">
                <div class="section-title">Apply these rules in Stock Manager</div>
                <p class="section-help">
                  When off, editing retail price 1 only changes price 1. Prices 2-10 stay exactly as they are in IQ.
                </p>
              </div>
              <app-switch-ui
                [ariaLabel]="'Enable multiple price change rules'"
                [checked]="draftSettings().enabled"
                [disabled]="!canRulesBeEdited"
                [size]="0.8"
                (checkedChange)="onToggleFeatureEnabled($event)">
              </app-switch-ui>
            </div>
          </section>

          <section class="preview-card">
            <div class="preview-heading-row">
              <div class="preview-heading">Try an item</div>
              <p class="preview-help">Shows what each configured price becomes for a given cost and retail price.</p>
            </div>
            <div class="preview-row">
              <div class="preview-input-block">
                <label for="multiple-price-preview-cost">Latest cost</label>
                <div class="preview-input-wrap">
                  <span class="preview-currency">R</span>
                  <input
                    id="multiple-price-preview-cost"
                    type="number"
                    min="0"
                    step="0.01"
                    [value]="previewCost()"
                    (input)="onPreviewCostInput($event)"/>
                </div>
              </div>
              <div class="preview-input-block">
                <label for="multiple-price-preview-price">Retail price 1 (incl)</label>
                <div class="preview-input-wrap">
                  <span class="preview-currency">R</span>
                  <input
                    id="multiple-price-preview-price"
                    type="number"
                    min="0"
                    step="0.01"
                    [value]="previewPrice()"
                    (input)="onPreviewPriceInput($event)"/>
                </div>
              </div>
              <div class="preview-input-block">
                <label for="multiple-price-preview-target-gp">Target GP %</label>
                <div class="preview-input-wrap">
                  <span class="preview-currency">%</span>
                  <input
                    id="multiple-price-preview-target-gp"
                    type="number"
                    min="0"
                    step="0.01"
                    [value]="previewTargetGp()"
                    (input)="onPreviewTargetGpInput($event)"/>
                </div>
              </div>
              <div class="preview-gp-block">
                <span class="preview-result-label">Price 1 GP</span>
                <strong class="preview-result-value">{{ previewPriceOneGp() }}%</strong>
              </div>
            </div>

            @if (previewResults().length === 0) {
              <p class="section-help">No prices change for this example. Add a rule, or check the condition on each price.</p>
            } @else {
              <table class="preview-table">
                <thead>
                <tr>
                  <th scope="col">Price</th>
                  <th scope="col">GP %</th>
                  <th scope="col">Incl</th>
                  <th scope="col">Excl</th>
                  <th scope="col">Outcome</th>
                </tr>
                </thead>
                <tbody>
                  @for (result of previewResults(); track result.priceIndex) {
                    <tr>
                      <td>Price {{ result.priceIndex }}</td>
                      <td>{{ result.effectiveGp }}%</td>
                      <td>{{ formatMoney(result.inclusive) }}</td>
                      <td>{{ formatMoney(result.exclusive) }}</td>
                      <td>
                        {{ result.conditionMet ? 'Condition met' : 'Alternative outcome' }}
                        @if (result.priceBandingApplied) {
                          <span class="preview-result-note">price banded</span>
                        }
                      </td>
                    </tr>
                  }
                </tbody>
              </table>
            }
          </section>

          <section class="rules-toolbar">
            <app-shared-button
              [colour]="'primary'"
              [disabled]="!canRulesBeEdited || availablePriceIndexes().length === 0"
              [fill]="true"
              (buttonClicked)="onAddPriceRule()">
              Add price rule
            </app-shared-button>
            <app-shared-button
              [colour]="'secondary'"
              [disabled]="!canRulesBeEdited || rules().length === 0"
              (buttonClicked)="onClearAllRules()">
              Clear All Rules
            </app-shared-button>
          </section>

          @if (rules().length === 0) {
            <section class="empty-state">
              No prices are linked to retail price 1 yet. Add a price rule to get started.
            </section>
          }

          @for (rule of rules(); track rule.priceIndex) {
            <article class="rule-card" [class.rule-card-open]="expandedPriceIndex() === rule.priceIndex">
              <header class="rule-header">
                <button type="button" class="rule-toggle" (click)="onToggleRuleExpanded(rule.priceIndex)">
                  <app-display-icon
                    [icon]="expandedPriceIndex() === rule.priceIndex ? icons.chevronUp : icons.chevronDown">
                  </app-display-icon>
                  <div class="rule-title-block">
                    <div class="rule-title">Price {{ rule.priceIndex }}</div>
                    @if (expandedPriceIndex() !== rule.priceIndex) {
                      <div class="rule-summary">{{ describeRule(rule) }}</div>
                    }
                  </div>
                </button>
                <app-switch-ui
                  [ariaLabel]="'Enable rule for price ' + rule.priceIndex"
                  [checked]="rule.enabled"
                  [disabled]="!canRulesBeEdited"
                  [size]="0.7"
                  (checkedChange)="onToggleRuleEnabled(rule.priceIndex, $event)">
                </app-switch-ui>
                <button
                  type="button"
                  class="icon-button"
                  [disabled]="!canRulesBeEdited"
                  aria-label="Remove price rule"
                  (click)="onRemovePriceRule(rule.priceIndex)">
                  <app-display-icon
                    [colour]="canRulesBeEdited ? iconColours.secondary : iconColours.disabled"
                    [icon]="icons.deleteOutline">
                  </app-display-icon>
                </button>
              </header>

              @if (expandedPriceIndex() === rule.priceIndex) {
                <div class="rule-body">
                  <div class="section-title">When should this price change?</div>
                  <div class="condition-row">
                    <div class="form-control-div">
                      <label>Compare</label>
                      <app-shared-custom-select
                        (selectionChange)="onConditionSourceChange(rule.priceIndex, $event)"
                        [allowMultiple]="false"
                        [allowSelectAll]="false"
                        [isDisabled]="!canRulesBeEdited"
                        [maxSelections]="1"
                        [placeholder]="'Select GP to compare'"
                        [setOptions]="conditionSourceOptions(rule)"
                        [showActionButtons]="false"
                        [showChips]="false"
                        [showSearchBar]="false">
                      </app-shared-custom-select>
                    </div>
                    <div class="form-control-div">
                      <label>Condition</label>
                      <app-shared-custom-select
                        (selectionChange)="onConditionOperatorChange(rule.priceIndex, $event)"
                        [allowMultiple]="false"
                        [allowSelectAll]="false"
                        [isDisabled]="!canRulesBeEdited"
                        [maxSelections]="1"
                        [placeholder]="'Select condition'"
                        [setOptions]="operatorOptions(rule)"
                        [showActionButtons]="false"
                        [showChips]="false"
                        [showSearchBar]="false">
                      </app-shared-custom-select>
                    </div>
                    @if (rule.condition.operator !== 'always') {
                      <div class="form-control-div">
                        <label [for]="'condition-value-' + rule.priceIndex">GP % to compare</label>
                        <input
                          [id]="'condition-value-' + rule.priceIndex"
                          type="number"
                          step="0.01"
                          [disabled]="!canRulesBeEdited"
                          [value]="rule.condition.value"
                          (input)="onConditionValueChange(rule.priceIndex, $event)"/>
                      </div>
                    }
                  </div>

                  <div class="section-title branch-title">
                    {{ rule.condition.operator === 'always' ? 'Always use' : 'When the condition is met' }}
                  </div>
                  <div class="branch-row">
                    <div class="form-control-div">
                      <label>GP source</label>
                      <app-shared-custom-select
                        (selectionChange)="onBranchModeChange(rule.priceIndex, 'whenTrue', $event)"
                        [allowMultiple]="false"
                        [allowSelectAll]="false"
                        [isDisabled]="!canRulesBeEdited"
                        [maxSelections]="1"
                        [placeholder]="'Select GP source'"
                        [setOptions]="modeOptions(rule, 'whenTrue')"
                        [showActionButtons]="false"
                        [showChips]="false"
                        [showSearchBar]="false">
                      </app-shared-custom-select>
                    </div>
                    @if (rule.whenTrue.mode !== 'none') {
                      <div class="form-control-div">
                        <label [for]="'when-true-value-' + rule.priceIndex">
                          {{ rule.whenTrue.mode === 'fixed' ? 'Fixed GP %' : 'Offset from price 1 GP %' }}
                        </label>
                        <input
                          [id]="'when-true-value-' + rule.priceIndex"
                          type="number"
                          step="0.01"
                          [disabled]="!canRulesBeEdited"
                          [value]="rule.whenTrue.value"
                          (input)="onBranchValueChange(rule.priceIndex, 'whenTrue', $event)"/>
                      </div>
                      <div class="form-control-div switch-control">
                        <label>Apply price banding</label>
                        <app-switch-ui
                          [ariaLabel]="'Apply price banding to price ' + rule.priceIndex"
                          [checked]="rule.whenTrue.priceBanding"
                          [disabled]="!canRulesBeEdited"
                          [size]="0.7"
                          (checkedChange)="onBranchPriceBandingChange(rule.priceIndex, 'whenTrue', $event)">
                        </app-switch-ui>
                      </div>
                    }
                  </div>

                  @if (rule.condition.operator !== 'always') {
                    <div class="section-title branch-title">Otherwise</div>
                    <div class="branch-row">
                      <div class="form-control-div">
                        <label>GP source</label>
                        <app-shared-custom-select
                          (selectionChange)="onBranchModeChange(rule.priceIndex, 'whenFalse', $event)"
                          [allowMultiple]="false"
                          [allowSelectAll]="false"
                          [isDisabled]="!canRulesBeEdited"
                          [maxSelections]="1"
                          [placeholder]="'Select GP source'"
                          [setOptions]="modeOptions(rule, 'whenFalse')"
                          [showActionButtons]="false"
                          [showChips]="false"
                          [showSearchBar]="false">
                        </app-shared-custom-select>
                      </div>
                      @if (rule.whenFalse.mode !== 'none') {
                        <div class="form-control-div">
                          <label [for]="'when-false-value-' + rule.priceIndex">
                            {{ rule.whenFalse.mode === 'fixed' ? 'Fixed GP %' : 'Offset from price 1 GP %' }}
                          </label>
                          <input
                            [id]="'when-false-value-' + rule.priceIndex"
                            type="number"
                            step="0.01"
                            [disabled]="!canRulesBeEdited"
                            [value]="rule.whenFalse.value"
                            (input)="onBranchValueChange(rule.priceIndex, 'whenFalse', $event)"/>
                        </div>
                        <div class="form-control-div switch-control">
                          <label>Apply price banding</label>
                          <app-switch-ui
                            [ariaLabel]="'Apply price banding to the alternative outcome for price ' + rule.priceIndex"
                            [checked]="rule.whenFalse.priceBanding"
                            [disabled]="!canRulesBeEdited"
                            [size]="0.7"
                            (checkedChange)="onBranchPriceBandingChange(rule.priceIndex, 'whenFalse', $event)">
                          </app-switch-ui>
                        </div>
                      }
                    </div>
                  }
                </div>
              }
            </article>
          }
        </div>

        <div actions class="widget-actions">
          @if (haveAnyEditsBeenMade) {
            <button type="button" class="action-button" (click)="onRevertForm()">
              <app-display-icon
                [colour]="iconColours.secondary"
                [tooltip]="tooltips.undoAllChanges"
                [icon]="icons.undo">
              </app-display-icon>
            </button>
          }
          @if (canRulesBeSubmitted) {
            <button type="button" class="action-button" [disabled]="isSubmitDisabled" (click)="onSubmitForm()">
              <app-display-icon
                [colour]="isSubmitDisabled ? iconColours.disabled : iconColours.secondary"
                [tooltip]="tooltips.saveChanges"
                [icon]="icons.contentSaveOutline">
              </app-display-icon>
            </button>
          }
        </div>
      </app-shared-widget-container-component>
    }
  </div>

  <div class="ups-container-action-bar widget-actions">
    @if (validationErrors().length > 0) {
      @for (error of validationErrors(); track $index) {
        <button type="button" class="error-button">
          <app-display-icon
            [colour]="iconColours.red"
            [tooltip]="error"
            [icon]="icons.alertRhombusOutline">
          </app-display-icon>
        </button>
      }
    }
    <button type="button" [disabled]="!haveAnyEditsBeenMade" class="action-button" (click)="onRevertForm()">
      <app-display-icon
        [colour]="iconColours.secondary"
        [tooltip]="tooltips.undoAllChanges"
        [icon]="icons.undo">
      </app-display-icon>
      Undo Changes
    </button>
    <button type="button" [disabled]="isSubmitDisabled" class="action-button" (click)="onSubmitForm()">
      <app-display-icon
        [colour]="iconColours.secondary"
        [tooltip]="tooltips.saveAllChanges"
        [icon]="icons.contentSaveOutline">
      </app-display-icon>
      @if (isSaving()) {
        Saving…
      } @else {
        Save All
      }
    </button>
  </div>
</div>
